import { InjectionToken } from '@angular/core';

/**
 * Which revision of the Terms of Service and of the DPA the consent step is displaying.
 *
 * **Why these are versions and not booleans.** The contract's consent payload carries document
 * versions, because "they agreed" only means something as "they agreed to *this text*", and the
 * version is the evidence a boolean would merely duplicate. Which means the form has to know which
 * version it just showed — and if it guesses, the stored acceptance names a document the customer
 * never saw.
 *
 * **Why they are injected rather than constants.** The texts change without this library changing,
 * and the version is a fact about the deployed legal documents rather than about the form. A host
 * that revises its Terms of Service overrides this token; nothing in the library has to be rebuilt.
 * The defaults are here so that a developer running the funnel locally gets a working form rather
 * than an empty required field.
 *
 * Whoever changes a default must change the document at the same time. A version bumped without the
 * text changing records an acceptance of a revision that does not exist; a text changed without the
 * version bumped records every future acceptance against the wrong revision, which is worse,
 * because it is the case an audit cannot detect.
 */
export interface LegalDocumentVersions {
  termsOfServiceVersion: string;
  dataProcessingAgreementVersion: string;
  /** Where the displayed Terms of Service can be read. Rendered as a link next to the checkbox. */
  termsOfServiceUrl: string;
  dataProcessingAgreementUrl: string;
}

export const LEGAL_DOCUMENT_VERSIONS = new InjectionToken<LegalDocumentVersions>('LEGAL_DOCUMENT_VERSIONS', {
  providedIn: 'root',
  factory: () => ({
    termsOfServiceVersion: '2026-09-01',
    dataProcessingAgreementVersion: '2026-09-01',
    termsOfServiceUrl: 'https://processpuzzle.com/legal/terms',
    dataProcessingAgreementUrl: 'https://processpuzzle.com/legal/dpa',
  }),
});
